import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import PickupLocation from ".";
import { GetBookingsQuery } from "../../__generated__/graphql";
import { useNotifications } from "../Notification";
import styles from "./PickupLocation.module.css";
import { PickupAnswer, PickupLocationType } from "./types";

export default function EditPickupLocation({
  open,
  handleClose,
  booking,
}: {
  open: boolean;
  handleClose: () => void;
  booking: GetBookingsQuery["bookings"]["records"][number];
}) {
  const { showNotification } = useNotifications();

  useEffect(() => {
    if (open) {
      setPickupLocation(null);
      setPickUpAnswer(PickupAnswer.IKNOW);
      setHasChanged(false);
      setSendEmailToCustomer(false);
    }
  }, [open]);

  const [pickupLocation, setPickupLocation] = useState<PickupLocationType>(
    booking.pickupLocation
      ? {
          ...booking.pickupLocation,
          query: {
            placeId: booking.pickupLocation.query?.placeId || "",
            geoLocation: booking.pickupLocation.query?.geoLocation || {
              lat: 0,
              lng: 0,
            },
          },
        }
      : null
  );

  const [pickUpAnswer, setPickUpAnswer] = useState<PickupAnswer>(
    booking.pickupLocation ? PickupAnswer.IKNOW : PickupAnswer.IDK
  );
  const [hasChanged, setHasChanged] = useState(false);
  const [sendEmailToCustomer, setSendEmailToCustomer] = useState(false);

  const [updatePickupLocation, { loading: submitting }] = useMutation(
    UPDATE_PICKUP_LOCATION,
    {
      onCompleted: () => {
        showNotification({
          severity: "success",
          message: "Pickup location updated",
        });
      },
      onError: (error: Error) => {
        showNotification({
          severity: "error",
          message: `Failed to update pickup location ${error.message}`,
        });
      },
    }
  );

  const handleSave = () => {
    if (!hasChanged) return;
    updatePickupLocation({
      variables: {
        input: {
          bookingId: booking.id,
          pickupLocation: pickupLocation,
          sendEmailToCustomer: sendEmailToCustomer,
        },
      },
      refetchQueries: ["GetBookings"],
    });
    handleClose();
  };

  const canSave =
    hasChanged && (pickupLocation || pickUpAnswer === PickupAnswer.IDK);

  return (
    <>
      {open && <div onClick={handleClose} className={styles.EditContainer} />}
      <dialog open={open} className={styles.EditDialog}>
        <DialogTitle>Edit Pickup Location</DialogTitle>
        <div className={styles.BookingIdContainer}>
          <strong>Booking ID: </strong>
          <span className={styles.BookingId}>{booking.id}</span>
        </div>
        <DialogContent>
          <PickupLocation
            hasAccomodationVehiclePickup={true}
            hasDefaultVehiclePickup={true}
            setPickupLocation={setPickupLocation}
            pickupLocation={pickupLocation}
            defaultMeetingPointQuery={
              booking.group.product.meetingPlaceGoogleMapsQuery ?? {
                id: "",
                placeId: "",
                geoLocation: { lat: 0, lng: 0 },
              }
            }
            defaultMeetingPlaceName={
              booking.group.product.translation?.meetingPlaceName || ""
            }
            pickupLocationZone={
              booking.group.product.pickupLocationZone ?? {
                center: { lat: 0, lng: 0 },
                radius: 0,
              }
            }
            pickUpAnswer={pickUpAnswer}
            setPickUpAnswer={setPickUpAnswer}
            onChange={setHasChanged}
            action="edit"
          />
        </DialogContent>
        <DialogActions>
          {canSave && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={sendEmailToCustomer}
                  onChange={() => setSendEmailToCustomer(!sendEmailToCustomer)}
                />
              }
              label={
                <span style={{ fontSize: "12px" }}>
                  Also send an email to the customer (<b>{booking.email}</b>)
                </span>
              }
            />
          )}
          <Button onClick={handleClose} style={{ color: "#009688" }}>
            Cancel
          </Button>
          {canSave && (
            <Button
              onClick={handleSave}
              style={{
                backgroundColor: "#009688",
              }}
              variant="contained"
              disabled={submitting}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </dialog>
    </>
  );
}

const UPDATE_PICKUP_LOCATION = gql(`
  mutation UpdatePickupLocation($input: UpdatePickupLocationInput!) {
    updatePickupLocation(input: $input)
  }
`);
